import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Phone, Download, ChevronDown } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import logoMark from '@/assets/favicon-exact.png';
import logoFull from '@/assets/logo-nahar-chandi-valley.png';
import LeadFormModal from '@/components/LeadFormModal';
import PremiumAccessBadge from '@/components/PremiumAccessBadge';

// Primary nav links (always visible) - Max 6 for conversion-first IA
const primaryNavLinks = [
  { label: 'Home', href: '/' },
  { label: 'Pricing & Plans', href: '/price' },
  { label: 'Layouts', href: '/floor-plans' },
  { label: 'Amenities', href: '/amenities' },
  { label: 'Location', href: '/location' },
  { label: 'Contact', href: '/contact' },
];

// Secondary nav links (in "More" dropdown on desktop)
const secondaryNavLinks = [
  { label: 'Gallery', href: '/gallery' },
  { label: 'Virtual Tour', href: '/virtual-tour' },
  { label: 'Compare Projects', href: '/compare' },
  { label: 'FAQs', href: '/faq' },
  { label: "Who It's For", href: '/who-its-for' },
  { label: 'Neighbourhood', href: '/neighbourhood' },
];

const allNavLinks = [...primaryNavLinks, ...secondaryNavLinks];

// Check for reduced motion preference
const prefersReducedMotion = typeof window !== 'undefined' 
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface HeaderProps {
  ribbonVisible?: boolean;
}

const Header = ({ ribbonVisible = false }: HeaderProps) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [isBrochureModalOpen, setIsBrochureModalOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setHasAnimated(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const isActive = (href: string) => location.pathname === href;
  const isSecondaryActive = secondaryNavLinks.some(link => isActive(link.href));

  // Animation variants
  const logoVariants = {
    hidden: { opacity: 0, y: 6 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const }
    }
  };

  const navVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.4, delay: 0.15, ease: "easeOut" as const }
    }
  };

  const ctaVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.4, delay: 0.25, ease: "easeOut" as const }
    }
  };

  const shouldAnimate = !prefersReducedMotion && !hasAnimated;

  return (
    <>
      {/* Skip to main content - accessibility link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[70] focus:bg-accent focus:text-accent-foreground focus:px-4 focus:py-2 focus:rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
      >
        Skip to main content
      </a>
      
      {/* Campaign Ribbon - Now handled by DismissibleCampaignRibbon in Index */}
      
      <header
        id="site-header"
        className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-primary py-2'
            : 'bg-primary shadow-md py-3'
        }`}
        style={{ 
          top: ribbonVisible ? '36px' : '0px',
          boxShadow: isScrolled 
            ? '0 4px 20px rgba(212, 175, 55, 0.15), 0 1px 0 rgba(212, 175, 55, 0.3)' 
            : undefined,
        }}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between gap-4">
          
          {/* ========== BRAND LOCKUP (Priority Element) ========== */}
          <motion.div
            initial={shouldAnimate ? 'hidden' : 'visible'}
            animate="visible"
            variants={logoVariants}
            className="flex-shrink-0"
          >
            <Link to="/" className="flex items-center gap-3 group">
              {/* Logo Mark (Tree Symbol) with gold glow */}
              <div 
                className={`rounded-lg transition-all duration-300 ${
                  isScrolled ? 'p-1' : 'p-1.5'
                }`}
                style={{
                  boxShadow: '0 0 12px rgba(201, 169, 98, 0.4), 0 0 0 1px rgba(201, 169, 98, 0.3)',
                  background: 'rgba(255, 255, 255, 0.05)',
                }}
              >
                <img 
                  src={logoMark} 
                  alt="" 
                  aria-hidden="true"
                  className={`object-contain transition-all duration-300 ${
                    isScrolled ? 'h-9 w-9' : 'h-10 w-10'
                  }`}
                />
              </div>
              
              {/* Text Wordmark */}
              <div className="flex flex-col justify-center leading-tight">
                <span 
                  className={`font-sans font-semibold tracking-[0.12em] uppercase text-accent-light transition-all duration-300 ${
                    isScrolled ? 'text-[10px]' : 'text-[11px]'
                  }`}
                >
                  Nahar's
                </span>
                <span 
                  className={`font-display font-bold text-primary-foreground transition-all duration-300 ${
                    isScrolled ? 'text-lg' : 'text-xl'
                  }`}
                  style={{ letterSpacing: '-0.01em' }}
                >
                  Chandi Valley
                </span>
              </div>
            </Link>
          </motion.div>

          {/* ========== DESKTOP NAVIGATION ========== */}
          <motion.nav 
            className="hidden lg:flex items-center gap-1 px-2 py-1.5 rounded-full bg-black/60 backdrop-blur-md border border-white/10 shadow-lg"
            initial={shouldAnimate ? 'hidden' : 'visible'}
            animate="visible"
            variants={navVariants}
          >
            {primaryNavLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`nav-text px-4 py-2 transition-all duration-200 rounded-full whitespace-nowrap ${
                  isActive(link.href)
                    ? 'text-accent-light bg-white/15 font-medium'
                    : 'text-white hover:text-accent-light hover:bg-white/10'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* More Dropdown - Keeps header clean */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button 
                  className={`nav-text flex items-center gap-1.5 px-4 py-2 transition-all duration-200 rounded-full ${
                    isSecondaryActive
                      ? 'text-accent-light bg-white/15 font-medium'
                      : 'text-white hover:text-accent-light hover:bg-white/10'
                  }`}
                  aria-label="More navigation options"
                >
                  More
                  <ChevronDown className="w-4 h-4" aria-hidden="true" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-primary/95 backdrop-blur-md border-white/10">
                {secondaryNavLinks.map((link) => (
                  <DropdownMenuItem key={link.href} asChild>
                    <Link
                      to={link.href}
                      className={`w-full text-[15px] ${isActive(link.href) ? 'text-accent-light' : 'text-primary-foreground'}`}
                    >
                      {link.label}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </motion.nav>

          {/* ========== PREMIUM ACCESS BADGE - ALL SCREEN SIZES ========== */}
          <PremiumAccessBadge className="text-[10px] sm:text-xs lg:text-sm px-2 py-1 sm:px-3 sm:py-1.5" />

          {/* ========== DESKTOP CTA BUTTONS ========== */}
          <motion.div 
            className="hidden lg:flex items-center gap-3"
            initial={shouldAnimate ? 'hidden' : 'visible'}
            animate="visible"
            variants={ctaVariants}
          >
            
            <Button
              variant="outline"
              size="sm"
              className="border-accent-light text-accent-light hover:bg-accent hover:text-accent-foreground font-medium"
              onClick={() => setIsBrochureModalOpen(true)}
              aria-label="Download brochure"
            >
              <Download className="w-4 h-4 mr-1.5" aria-hidden="true" />
              Brochure
            </Button>
            <Link to="/contact">
              <Button size="sm" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-gold" aria-label="Contact us">
                <Phone className="w-4 h-4 mr-1.5" aria-hidden="true" />
                Contact
              </Button>
            </Link>
          </motion.div>

          {/* ========== MOBILE MENU ========== */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" className="text-primary-foreground flex-shrink-0" aria-label="Open navigation menu">
                <Menu className="w-6 h-6" aria-hidden="true" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] max-w-80 bg-primary border-primary p-0 overflow-y-auto">
              <div className="flex flex-col min-h-full">
                {/* Logo - preloaded, no animation delay */}
                <div className="px-6 pt-6 pb-4 border-b border-white/10">
                  <div className="flex items-center justify-between gap-3">
                    <div className="bg-white rounded-xl p-3 inline-block shadow-md">
                      <img 
                        src={logoFull} 
                        alt="Nahar's Chandi Valley" 
                        className="h-12 w-auto object-contain"
                        loading="eager"
                        fetchPriority="high"
                      />
                    </div>
                    {/* Premium Access Badge in mobile */}
                    <PremiumAccessBadge className="text-[10px]" />
                  </div>
                </div>
                
                {/* Navigation Links */}
                <nav className="flex flex-col gap-1 px-4 py-4 flex-1">
                  {allNavLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setIsOpen(false)}
                      className={`px-4 py-3.5 text-base font-medium transition-colors rounded-lg ${
                        isActive(link.href)
                          ? 'bg-accent text-accent-foreground'
                          : 'text-primary-foreground hover:bg-white/10'
                      }`}
                    >
                      {link.label}
                    </Link>
                  ))}
                </nav>
                
                {/* CTA Buttons - Fixed at bottom */}
                <div className="px-4 pb-6 pt-4 flex flex-col gap-3 border-t border-white/10 bg-primary">
                  <Button 
                    className="w-full bg-accent text-accent-foreground hover:bg-accent/90 py-5 text-base"
                    onClick={() => {
                      setIsOpen(false);
                      setIsBrochureModalOpen(true);
                    }}
                  >
                    <Download className="w-4 h-4 mr-2" aria-hidden="true" />
                    Download Brochure
                  </Button>
                  <Link to="/contact" onClick={() => setIsOpen(false)} className="w-full">
                    <Button variant="outline" className="w-full border-accent-light text-accent-light py-5 text-base font-medium">
                      <Phone className="w-4 h-4 mr-2" aria-hidden="true" />
                      Contact Us
                    </Button>
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      </header>

      <LeadFormModal 
        isOpen={isBrochureModalOpen} 
        onClose={() => setIsBrochureModalOpen(false)} 
        type="brochure" 
      />
    </>
  );
};

export default Header;
